import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { COLORS } from '@/styles/colors';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { FormikInput } from '@/components/form/fields/FormikInput/FormikInput';
import { NavigationLink } from '@/components/NavigationLink/NavigationLink';
import { Button } from '@/components/Button/Button';
import { useFormik } from 'formik';
import { envConfig } from '@/config/envConfig';
import {
  SIGN_UP_INITIAL_VALUES,
  SIGN_UP_VALIDATION_SCHEMA,
  SignUpValidationSchemaType,
} from '@/bundle/Auth/AdminOnboardingFlow/SignUpPage/const';
import { useMutation } from '@tanstack/react-query';
import { GoogleReCaptcha, GoogleReCaptchaType } from '@/components/GoogleReCaptcha/GoogleReCaptcha';
import { useRef, useState } from 'react';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { Notification } from '@/components/Notification/Notification';
import {
  CreateUnauthorizedOrganizationPayloadType,
  CreateUnauthorizedOrganizationResponseType,
} from '@/api/v1/organization/unauthorized/createUnauthorizedOrganizationApi';
import { createUnauthorizedOrganization } from '../api';
import { SignUpFormWrapper } from './styles';

type SignUpFormPropsType = {
  onSuccess: (data: { response: CreateUnauthorizedOrganizationResponseType; email: string }) => void;
};

export const SignUpForm = ({ onSuccess }: SignUpFormPropsType) => {
  const recaptchaRef = useRef<GoogleReCaptchaType>(null);
  const [captchaLoading, setCaptchaLoading] = useState(false);

  const { mutate, data, isPending } = useMutation({
    mutationKey: ['create_unauthorized_organization'],
    mutationFn: (payload: CreateUnauthorizedOrganizationPayloadType) => {
      return createUnauthorizedOrganization(payload);
    },
    onSuccess(response, variables) {
      if (response?.error) {
        recaptchaRef.current?.resetCaptcha();

        return;
      }

      onSuccess({ response: response?.body, email: variables?.user?.email });
    },
    retry: false,
  });

  const formik = useFormik<SignUpValidationSchemaType>({
    initialValues: SIGN_UP_INITIAL_VALUES,
    validationSchema: SIGN_UP_VALIDATION_SCHEMA,
    onSubmit: (values) => {
      setCaptchaLoading(true);

      recaptchaRef.current
        ?.executeCaptcha()
        .then((token) => {
          setCaptchaLoading(false);

          const payload = {
            recaptcha_response: token,
            ...values,
          };

          mutate(payload);
        })
        .catch((error) => {
          console.error('google captcha failed with', error?.message);

          setCaptchaLoading(false);
        });
    },
  });

  const apiError = data?.error;
  const formError = getResponseError(apiError) || getResponseError(apiError, 'recaptcha_response');

  return (
    <SignUpFormWrapper>
      {formError && (
        <Notification variant='error' marginBottom='24px'>
          {formError}
        </Notification>
      )}

      <FormikForm value={formik}>
        <FormikInput
          name='user.email'
          label='Email Address*'
          placeholder='Enter Email Address'
          apiError={apiError}
          hint='Email is used to help keep your account and wire instructions secure.'
        />
        <FormikInput name='user.first_name' label='First Name*' placeholder='Enter First Name' apiError={apiError} />
        <FormikInput name='user.last_name' label='Last Name*' placeholder='Enter Last Name' apiError={apiError} />
        <FormikInput
          name='organization.name'
          label='Organization Name'
          placeholder='Enter Organization Name'
          apiError={apiError}
        />
        <GoogleReCaptcha ref={recaptchaRef} />
        <Box display='flex' flexDirection='column' alignItems='center' rowGap='32px'>
          <Box>
            <Typography color={COLORS.black}>
              By clicking, you agree to{' '}
              <NavigationLink to={envConfig.TERMS_AND_CONDITIONS_URL} size='large' target='_blank'>
                Terms & Conditions
              </NavigationLink>{' '}
              and{' '}
              <NavigationLink to={envConfig.PRIVACY_POLICY_URL} size='large' target='_blank'>
                Privacy Policy
              </NavigationLink>
              .
            </Typography>
          </Box>
          <Box width='100%' display='flex' flexDirection='column' alignItems='center' rowGap='20px'>
            <Button variant='primary' type='submit' isLoading={isPending || captchaLoading}>
              Continue
            </Button>

            <Typography variant='bodySmall' color={COLORS.grey[700]}>
              Your first transaction takes less than 5 minutes to set up.
            </Typography>
          </Box>
        </Box>
      </FormikForm>
    </SignUpFormWrapper>
  );
};
